/**
 * NewWorkflowModal — small modal for creating a workflow inside an
 * existing project. Triggered by the "+" button on the project header
 * in ProjectTree. Asks the user for a workflow name + optional BRD and
 * data-mapping uploads. The parent owns the upload + createDesign +
 * navigate flow (it has consultant id + email + router access).
 *
 * Submit payload shape: `{ name, brdFile, dataMappingFile }`.
 *
 * Why a separate modal from NewProjectModal: the project modal is about
 * a long-lived per-account context (name + KB shared across every
 * workflow). A workflow is short-lived and scoped to a single BRD. The
 * fields don't overlap, and conflating them made the UX ambiguous —
 * users couldn't tell whether files were going into KB (account-shared)
 * or inputs/ (workflow-scoped).
 */
import React, { useEffect, useRef, useState } from 'react';
import FileTypeBadge from './FileTypeBadge';
import { formatBytes } from './chat/renderMessage';
import CustomerNameField from './CustomerNameField';
import { listDesigns } from '../services/api';
import { useAuth } from '../AuthContext';

const BRD_ACCEPT = '.pdf,.docx,.doc';
const DM_ACCEPT = '.xlsx,.xls,.csv';

export default function NewWorkflowModal({
  open,
  projectName = '',
  initialCustomerName = '',
  designs = null,
  onClose,
  onCreate,
  busy = false,
}) {
  const { user } = useAuth();
  const consultantId = user?.email || null;
  const [name, setName] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [allDesigns, setAllDesigns] = useState([]);
  const [brdFile, setBrdFile] = useState(null);
  const [dmFile, setDmFile] = useState(null);
  const [error, setError] = useState('');
  const nameRef = useRef(null);
  const brdInputRef = useRef(null);
  const dmInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setName('');
    setCustomerName(initialCustomerName || '');
    setBrdFile(null);
    setDmFile(null);
    setError('');
    let cancelled = false;
    // Autocomplete options come from the parent when it already has the
    // designs list; only fetch as a fallback.
    if (consultantId && !designs) {
      listDesigns(consultantId)
        .then((data) => { if (!cancelled) setAllDesigns(data?.designs || []); })
        .catch(() => {});
    }
    setTimeout(() => nameRef.current?.focus(), 50);
    return () => { cancelled = true; };
  }, [open, consultantId, initialCustomerName, designs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = () => {
    if (busy) return;
    const trimmed = name.trim();
    const trimmedCustomer = customerName.trim();
    if (!trimmed) {
      setError('Workflow name is required.');
      nameRef.current?.focus();
      return;
    }
    if (!trimmedCustomer) {
      setError('Customer name is required.');
      return;
    }
    setError('');
    onCreate?.({ name: trimmed, customerName: trimmedCustomer, brdFile, dataMappingFile: dmFile });
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose?.();
      }}
    >
      <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="new-workflow-title">
        <div className="modal-header">
          <h2 id="new-workflow-title">
            New workflow{projectName ? ` in ${projectName}` : ''}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >×</button>
        </div>

        <div className="modal-body">
          <label className="modal-field">
            <span className="modal-label">Workflow name</span>
            <input
              ref={nameRef}
              type="text"
              className="modal-input"
              placeholder="e.g. Birthday Coupon — UC-01"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              disabled={busy}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </label>

          <div className="modal-field">
            <span className="modal-label">Customer name</span>
            <CustomerNameField
              value={customerName}
              onChange={setCustomerName}
              required
              designs={designs || allDesigns}
              disabled={busy}
              placeholder="e.g. Luma"
            />
          </div>

          <div className="modal-field">
            <span className="modal-label">
              BRD document
              <span className="modal-hint">optional — you can drop one in the chat composer later</span>
            </span>
            <div className="modal-drop modal-drop-list">
              {brdFile && (
                <div className="modal-file-chip">
                  <FileTypeBadge name={brdFile.name} />
                  <div className="modal-file-info">
                    <div className="modal-file-name">{brdFile.name}</div>
                    <div className="modal-file-meta">{formatBytes(brdFile.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="modal-file-remove"
                    onClick={() => setBrdFile(null)}
                    disabled={busy}
                    aria-label={`Remove ${brdFile.name}`}
                  >×</button>
                </div>
              )}
              {!brdFile && (
                <button
                  type="button"
                  className="modal-drop-btn"
                  onClick={() => brdInputRef.current?.click()}
                  disabled={busy}
                >+ Add BRD</button>
              )}
              <input
                ref={brdInputRef}
                type="file"
                hidden
                accept={BRD_ACCEPT}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setBrdFile(f);
                  if (e.target) e.target.value = '';
                }}
              />
            </div>
          </div>

          <div className="modal-field">
            <span className="modal-label">
              Data mapping
              <span className="modal-hint">optional — Excel/CSV with field mappings</span>
            </span>
            <div className="modal-drop modal-drop-list">
              {dmFile && (
                <div className="modal-file-chip">
                  <FileTypeBadge name={dmFile.name} />
                  <div className="modal-file-info">
                    <div className="modal-file-name">{dmFile.name}</div>
                    <div className="modal-file-meta">{formatBytes(dmFile.size)}</div>
                  </div>
                  <button
                    type="button"
                    className="modal-file-remove"
                    onClick={() => setDmFile(null)}
                    disabled={busy}
                    aria-label={`Remove ${dmFile.name}`}
                  >×</button>
                </div>
              )}
              {!dmFile && (
                <button
                  type="button"
                  className="modal-drop-btn"
                  onClick={() => dmInputRef.current?.click()}
                  disabled={busy}
                >+ Add data mapping</button>
              )}
              <input
                ref={dmInputRef}
                type="file"
                hidden
                accept={DM_ACCEPT}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) setDmFile(f);
                  if (e.target) e.target.value = '';
                }}
              />
            </div>
          </div>

          {error && <div className="modal-error">{error}</div>}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="modal-btn modal-btn-ghost"
            onClick={onClose}
            disabled={busy}
          >Cancel</button>
          <button
            type="button"
            className="modal-btn modal-btn-primary"
            onClick={submit}
            disabled={busy}
          >{busy ? 'Creating…' : 'Create workflow'}</button>
        </div>
      </div>
    </div>
  );
}
